import { normalizeAccountCode } from './accountCode.js'
import {
  buildSegmentIndex,
  isDistinctiveSegment,
  type SegmentIndex,
} from './aliases.js'

/**
 * Budget code lookup — REQUIREMENTS.md §7.5.
 *
 * A first-class workflow, not a drill-down: Matt is standing at a vendor
 * deciding whether to buy something, and needs the right code in under three
 * seconds. That means ranked results, not a filtered list — the single best
 * match should be first and obvious.
 *
 * Matching is case-insensitive across four surfaces, in priority order:
 *
 *   1. exact alias            `580` → Travel
 *   2. the account code       full or partial, digits and hyphens
 *   3. distinctive segment    `329` → Prof. Ed. Services (see aliases.ts)
 *   4. display name / label   `travel` → Travel — Innovation
 *
 * External codes are excluded by default; the lookup UI offers a toggle (§7.7).
 */

export interface SearchableCode {
  id: string
  code: string
  displayName: string
  shortLabel: string
  aliases: readonly string[]
  external: boolean
  active: boolean
}

/** Why a code matched — surfaced in the UI so results explain themselves. */
export type MatchReason =
  | 'alias'
  | 'alias-prefix'
  | 'code'
  | 'segment'
  | 'name-prefix'
  | 'name'

export interface LookupResult<T extends SearchableCode = SearchableCode> {
  code: T
  score: number
  reason: MatchReason
  /** Short explanation, e.g. `alias "580"` or `segment 3`. */
  detail: string
}

/**
 * Score per match kind. Gaps are wide so a weaker match can never outrank a
 * stronger one, whatever the tiebreakers.
 */
const SCORES: Record<MatchReason, number> = {
  alias: 100,
  code: 90,
  segment: 80,
  'alias-prefix': 70,
  'name-prefix': 60,
  name: 50,
}

export interface LookupOptions {
  /** Include `external: true` codes. Default false (§7.7). */
  includeExternal?: boolean
  /** Include soft-deleted codes. Default false. */
  includeInactive?: boolean
  /** Cap on returned results. Default 20. */
  limit?: number
}

/** Digits-and-hyphens only — the shape of an account code fragment. */
const CODE_FRAGMENT = /^[\d-]+$/

function bestMatch(
  code: SearchableCode,
  query: string,
  index: SegmentIndex,
  totalCodes: number,
  /**
   * Whether the query, treated as a raw code fragment, narrows the set. Same
   * half-the-codes rule that governs segments: `2818` sits in ten of eleven
   * codes and identifies nothing, so it must not match via the code branch
   * either. Computed once per query by the caller.
   */
  fragmentIsDistinctive: boolean,
): { reason: MatchReason; detail: string } | null {
  const q = query.toLowerCase()

  // 1. Exact alias — the strongest signal, because the user configured it.
  const exactAlias = code.aliases.find((a) => a.toLowerCase() === q)
  if (exactAlias !== undefined) {
    return { reason: 'alias', detail: `alias "${exactAlias}"` }
  }

  // 2. The account code itself, whole or partial. Compared with separators
  //    stripped so "2818580" and "2818-580" both work.
  if (CODE_FRAGMENT.test(q)) {
    const bare = q.replace(/-/g, '')
    const normalized = normalizeAccountCode(code.code) ?? code.code
    if (bare.length >= 2 && normalized.replace(/-/g, '').includes(bare)) {
      // A whole-code match outranks a segment match; a fragment that happens to
      // span segment boundaries lands here too.
      const segments = normalized.split('-')
      const asSegment = segments.findIndex(
        (value, position) =>
          value === q && isDistinctiveSegment(index, position, value, totalCodes),
      )
      if (asSegment !== -1) {
        return { reason: 'segment', detail: `segment ${asSegment + 1}` }
      }
      if (bare.length >= 4 && fragmentIsDistinctive) {
        return { reason: 'code', detail: 'account code' }
      }
    }
  }

  // 3. Alias prefix — partial typing of a configured alias.
  const aliasPrefix = code.aliases.find((a) => a.toLowerCase().startsWith(q))
  if (aliasPrefix !== undefined) {
    return { reason: 'alias-prefix', detail: `alias "${aliasPrefix}"` }
  }

  // 4. Display name and short label.
  const name = code.displayName.toLowerCase()
  const label = code.shortLabel.toLowerCase()
  if (name.startsWith(q) || label.startsWith(q)) {
    return { reason: 'name-prefix', detail: 'name' }
  }
  if (name.includes(q) || label.includes(q)) {
    return { reason: 'name', detail: 'name' }
  }

  return null
}

/**
 * Rank `codes` against `query`.
 *
 * An empty query returns the eligible set in display order rather than nothing,
 * so opening the lookup screen shows something useful before typing starts.
 */
export function lookupBudgetCodes<T extends SearchableCode>(
  query: string,
  codes: readonly T[],
  options: LookupOptions = {},
): LookupResult<T>[] {
  const {
    includeExternal = false,
    includeInactive = false,
    limit = 20,
  } = options

  const eligible = codes.filter(
    (c) => (includeExternal || !c.external) && (includeInactive || c.active),
  )

  const trimmed = query.trim()
  if (trimmed === '') {
    return eligible
      .slice()
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
      .slice(0, limit)
      .map((code) => ({ code, score: 0, reason: 'name' as const, detail: '' }))
  }

  // Distinctiveness is computed over the *eligible* set, so hiding external
  // codes doesn't leave a segment looking artificially common.
  const index = buildSegmentIndex(eligible)

  // How many codes contain this query as a raw digit fragment. A fragment
  // present in more than half the set narrows nothing and is not a match.
  const bare = trimmed.replace(/-/g, '')
  const fragmentMatches = CODE_FRAGMENT.test(trimmed)
    ? eligible.filter((c) =>
        (normalizeAccountCode(c.code) ?? c.code).replace(/-/g, '').includes(bare),
      ).length
    : 0
  const fragmentIsDistinctive =
    fragmentMatches > 0 && fragmentMatches <= eligible.length / 2

  return eligible
    .flatMap((code) => {
      const match = bestMatch(
        code,
        trimmed,
        index,
        eligible.length,
        fragmentIsDistinctive,
      )
      if (match === null) return []
      return [{ code, score: SCORES[match.reason], ...match }]
    })
    .sort(
      (a, b) =>
        b.score - a.score || a.code.displayName.localeCompare(b.code.displayName),
    )
    .slice(0, limit)
}

/**
 * The single unambiguous match for a query, or null.
 *
 * Used to jump straight to a code's detail view when the user types something
 * that can only mean one thing.
 */
export function resolveSingleCode<T extends SearchableCode>(
  query: string,
  codes: readonly T[],
  options: LookupOptions = {},
): T | null {
  const results = lookupBudgetCodes(query, codes, options)
  const first = results[0]
  if (first === undefined) return null
  const second = results[1]
  if (second !== undefined && second.score === first.score) return null
  return first.code
}
